// 調整舞台第 3 段：存起來的前端呼叫 — 對應 cf-worker/cueLibraryStage.mjs
// 權限（後台會檢查）：導演、這個專案的製作、全站製作；動畫師 403。每一條後台都只收下面列的欄位，多送別的 = 400。
// 跟 cueLibraryApi.ts 分開：那份是製作用的（改整個畫面、編號、刪除…），這份是導演用的（只能動機關位置、名字、轉場秒數）。

import { apiBase, getSessionToken } from '../api';
import { saveStatus } from '../saveStatus';

/** 一個機關在 Cue 庫的 cue 裡的位置（整份換掉時，沒改的也要原樣帶回去） */
export interface StageMechRow {
  mechanismId: string;
  value: number;
  delaySec: number | null;
  durationSec: number | null;
  /** 沿用：位置算畫面的一部分，但不打斷正在往這裡走的機器；有 carried 時不能有時間 */
  carried: boolean;
}

export interface StageSong { id: string; name: string; code: string | null }

/** 後台回的錯誤：message 是中文、可以直接顯示；409 + conflict = 剛剛被別人改過 */
export interface StageApiError extends Error {
  status: number;
  data: {
    error?: string;
    /** 409：這個 cue 剛剛被別人改過 */
    conflict?: boolean;
    /** 409：現在的版本 */
    rev?: number | null;
    /** 409：最後改的人（不知道 = null） */
    changedBy?: { id: string; name: string } | null;
    field?: string;
    [k: string]: unknown;
  };
}

export interface SaveBackResult {
  ok: true;
  /** 跟現在存的一樣，沒有東西要存 */
  unchanged?: boolean;
  /** 存完的新版本 */
  rev: number;
  /** 存之前的樣子（復原 = 用這個再存一次） */
  previous?: { rev: number; mechanisms: StageMechRow[] };
  /** 用到這個 cue 的歌（會一起變的） */
  songs: StageSong[];
  /** 幾個機關不一樣 */
  changed: number;
  notified: number;
}

export interface SaveAsResult {
  libraryCue: { id: string; label: string; name: string; basedOnId: string | null; rev: number; crossfadeSeconds: number };
  slot: { id: string; songId: string; libraryCueId: string };
  song: StageSong;
  notified: number;
}

export interface CopyResult {
  libraryCue: { id: string; label: string; name: string; basedOnId: string | null; rev: number; crossfadeSeconds: number };
}

const enc = encodeURIComponent;
const libPath = (projectId: string, lcId: string) => `/api/projects/${enc(projectId)}/library-cues/${enc(lcId)}`;

async function toError(resp: Response): Promise<StageApiError> {
  let data: StageApiError['data'] = {};
  try { data = await resp.json(); } catch { /* 不是 JSON */ }
  const e = new Error(data?.error || `HTTP ${resp.status}`) as StageApiError;
  e.status = resp.status;
  e.data = data || {};
  return e;
}

async function call<T>(path: string, method: string, body: unknown): Promise<T> {
  const h: Record<string, string> = body === undefined ? {} : { 'Content-Type': 'application/json' };
  const token = getSessionToken();
  if (token) h.Authorization = `Bearer ${token}`;
  saveStatus.saving();
  try {
    const resp = await fetch(apiBase() + path, { method, credentials: 'include', headers: h, body: body === undefined ? undefined : JSON.stringify(body) });
    if (!resp.ok) throw await toError(resp);
    const out = await resp.json() as T;
    saveStatus.done();
    return out;
  } catch (e) {
    saveStatus.done();
    // 409（被別人改過、這首歌的 cue 換掉了）是要問使用者的，不算存檔失敗
    if ((e as StageApiError).status !== 409) saveStatus.fail(e instanceof Error ? e.message : String(e));
    throw e;
  }
}

/** 後台說「剛剛被別人改過」（版本對不上） */
export function isStageConflict(e: unknown): e is StageApiError {
  const x = e as StageApiError | null;
  return !!x && x.status === 409 && x.data?.conflict === true;
}

/**
 * 存回 Cue 庫：只換這個 cue 的機關位置（整份）。ifRev = 拿到這些位置的時候這個 cue 的版本，
 * 這中間被別人改過 → 409（isStageConflict）。songId = 導演現在看的歌（通知點開哪一首）。undo = 這次是復原
 */
export function saveStagePositions(projectId: string, lcId: string, body: { ifRev: number; mechanisms: StageMechRow[]; songId?: string; undo?: boolean }): Promise<SaveBackResult> {
  return call(`${libPath(projectId, lcId)}/mechanism-positions`, 'PUT', body);
}

/** 另存成新 cue：編號自動給下一個，物件設定照抄，只把這首歌的這個 cue 換成新的 */
export function saveStageAs(projectId: string, lcId: string, body: { name?: string; mechanisms: StageMechRow[]; songId: string; cueId: string }): Promise<SaveAsResult> {
  return call(`${libPath(projectId, lcId)}/stage-save-as`, 'POST', body);
}

/** 複製：放進 Cue 庫的新 cue（還沒放進任何歌） */
export function copyStageCue(projectId: string, lcId: string, body: { name?: string }): Promise<CopyResult> {
  return call(`${libPath(projectId, lcId)}/stage-copy`, 'POST', body);
}

/** Cue 庫的 cue 被 PATCH 之後後台回的樣子（只列用得到的欄位） */
export interface PatchedCue {
  id: string; label: string; name: string; note?: string; crossfadeSeconds: number; startCueId?: string | null;
}

/**
 * 改名稱 / 編號 / 備註 / Cue 庫的轉場秒數（用到的歌一起變）/ 這個 cue 自己的起始（startCueId：別的 cue 的 id、null = 預設位置；0057）。
 * songId = 通知點開哪一首。編號重複 409、空白 400
 */
export function patchStageCue(projectId: string, lcId: string, body: { name?: string; label?: string; note?: string; crossfadeSeconds?: number; startCueId?: string | null; songId?: string }): Promise<{ libraryCue: PatchedCue; notified: number }> {
  return call(`${libPath(projectId, lcId)}/stage`, 'PATCH', body);
}

/** 刪 cue（導演、製作都可以）。還有歌在用 → 409，error.data.usage 列出在用的歌 */
export function deleteStageCue(projectId: string, lcId: string): Promise<{ ok: true; keepDays?: number }> {
  return call(`${libPath(projectId, lcId)}/stage`, 'DELETE', undefined);
}

/** Cue 庫頁左邊清單拖順序：一次寫整份（編號不變） */
export function putStageOrder(projectId: string, ids: string[]): Promise<{ ok: true; count: number }> {
  return call(`/api/projects/${enc(projectId)}/library-cues/order`, 'PUT', { ids });
}

/**
 * 改「這首歌的這個 cue 自己設的」轉場秒數（沒另外設的後台會擋，要改 Cue 庫的）。
 * opts.own === true：變成這首歌自己設的（時間軸上拉轉場條用）；opts.own === false：回到跟著 Cue 庫（復原用，秒數採用 Cue 庫的）
 * 回 previous = 改之前的樣子（復原：own 是 true 原樣送回，false 送 own: false）
 */
export function setSlotStageCrossfade(
  projectId: string, songId: string, cueId: string, crossfadeSeconds: number, opts: { own?: boolean } = {},
): Promise<{ ok: true; notified: number; previous?: { crossfadeSeconds: number; own: boolean } }> {
  return call(`/api/projects/${enc(projectId)}/songs/${enc(songId)}/cues/${enc(cueId)}/stage-crossfade`, 'PUT',
    { crossfadeSeconds, ...(opts.own === undefined ? {} : { own: opts.own }) });
}

// ───────── 導演頁 cue 列 / 時間軸：順序、對調、換成別的、拿掉、時間、歌曲長度、起始位置 ─────────
// 後台：cf-worker/cueLibraryStage.mjs（need = directorStage：導演、專案的製作、全站製作；動畫師 403）。Cue 庫專案才有。

/** 回來的 cue 順序（時間格留在原位，換的是內容）：每個 cue 現在的順序和時間 */
export type StageOrder = { id: string; order: number; videoTimeSec: number | null }[];

const songCues = (projectId: string, songId: string) => `/api/projects/${enc(projectId)}/songs/${enc(songId)}/cues`;

/** 兩個 cue 對調：兩個 cue 互相拿到對方的時間格，轉場跟著 cue。再對調一次就是復原 */
export function stageSwapCues(projectId: string, songId: string, cueIdA: string, cueIdB: string): Promise<{ ok: true; order: StageOrder; notified: number }> {
  return call(`${songCues(projectId, songId)}/stage-swap`, 'POST', { cueIdA, cueIdB });
}
/** 拖到兩個 cue 中間 = 插入：orderedIds 是這首歌全部正式 cue 的新順序，時間格留在原位。復原 = 把 previousOrderedIds 再送一次 */
export function stageRearrangeCues(projectId: string, songId: string, orderedIds: string[]): Promise<{ ok: true; order: StageOrder; previousOrderedIds: string[]; notified: number }> {
  return call(`${songCues(projectId, songId)}/stage-rearrange`, 'POST', { orderedIds });
}
/** 把這首歌的某個 cue 換成 Cue 庫的另一個 cue（時間不變，Cue 庫的 cue 不動，別首歌不受影響） */
export function stageReplaceCue(projectId: string, songId: string, cueId: string, libraryCueId: string): Promise<{ ok: true; previousLibraryCueId: string | null; notified: number }> {
  return call(`${songCues(projectId, songId)}/${enc(cueId)}/stage-replace`, 'PUT', { libraryCueId });
}
/** 從這首歌拿掉一個 cue（Cue 庫的 cue 不會被刪；綁在它上面的 LED 換版型點會一起消失）。復原 = 把 removed 丟給 addStageLibraryCue */
export function stageRemoveCue(projectId: string, songId: string, cueId: string): Promise<{ ok: true; removed: { libraryCueId: string; videoTimeSec: number | null; crossfadeSeconds: number | null }; notified: number }> {
  return call(`${songCues(projectId, songId)}/${enc(cueId)}/stage-remove`, 'POST', {});
}
/** 改一個 cue 的時間（null = 不對應影片時間）；時間重複 409。復原 = 用 previousTimeSec 再送一次 */
export function stageSetCueTime(projectId: string, songId: string, cueId: string, videoTimeSec: number | null): Promise<{ ok: true; order?: StageOrder; previousTimeSec: number | null; notified: number }> {
  return call(`${songCues(projectId, songId)}/${enc(cueId)}/stage-time`, 'PUT', { videoTimeSec });
}
/** 一次改好幾個 cue 的時間（全改或都不動）；復原 = 把 previous 再送一次。順序照回應的 order 畫 */
export function stageSetCueTimes(projectId: string, songId: string, times: { cueId: string; videoTimeSec: number | null }[]): Promise<{ ok: true; order: StageOrder; previous: { cueId: string; videoTimeSec: number | null }[]; changed: number; notified: number }> {
  return call(`${songCues(projectId, songId)}/stage-times`, 'PUT', { times });
}
/**
 * 改這首歌的起始位置 / 歌曲長度（PATCH songs/:s；導演、專案製作、全站製作）。
 * startMode：'prev' 接上一首 / 'library_cue' 指定 cue（要帶 startLibraryCueId）/ 'defaults' 機關預設位置；lengthSec：1~86400 秒，null = 清掉
 */
export function patchSongStage(
  projectId: string, songId: string, body: { startMode?: 'prev' | 'library_cue' | 'defaults'; startLibraryCueId?: string | null; lengthSec?: number | null },
): Promise<{ ok: true; startMode?: string; startLibraryCueId?: string | null; lengthSec?: number | null }> {
  return call(`/api/projects/${enc(projectId)}/songs/${enc(songId)}`, 'PATCH', body);
}

/**
 * 在這首歌最後面新增一個 cue（導演頁 cue 列的「新增 cue」）：空白，或複製這個專案的另一個 cue（cloneFrom）。
 * 後台只收這三個欄位；回 { ok, id, order }
 */
/** 從 Cue 庫選一個 cue 加進這首歌（導演和製作）；crossfadeSeconds 沒給 = 用 Cue 庫的轉場 */
export function addStageLibraryCue(projectId: string, songId: string, body: { libraryCueId: string; videoTimeSec: number | null; crossfadeSeconds?: number }): Promise<{ slot: { id: string }; notified?: number }> {
  return call(`/api/projects/${enc(projectId)}/songs/${enc(songId)}/cues/stage-add`, 'POST', body);
}

export function addStageCue(projectId: string, songId: string, body: { name: string; cloneFrom?: string; crossfadeSeconds?: number }): Promise<{ ok: true; id: string; order: number; notified?: number }> {
  return call(`/api/projects/${enc(projectId)}/songs/${enc(songId)}/cues/stage-add`, 'POST', body);
}
